import { SourcingHiringGoalWithMetrics } from 'api-clients/monolith';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import IconRight from '@/assets/icons/icon-right.svg';
import { HiringGoalModal } from '@/components/pages/channels/hiring-goals-modal';
import { AggregateDataCheckbox as AggregateHiresCheckbox } from '@/components/ui/aggregate-data-checkbox';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useOpeningContext } from '@/contexts/opening-context';
import { formatShortDate } from '@/utils/date-util';

import { HiringOpeningsPopover } from './hiring-openings-popover';
import { TimeframeInfo } from './timeframe-info';

interface HiringGoalProps {
  hiringGoal: SourcingHiringGoalWithMetrics | null;
  hireATHiringGoalUrl: string;
}

export function HiringGoal({
  hiringGoal,
  hireATHiringGoalUrl,
}: HiringGoalProps) {
  const intl = useIntl();
  const [isHiringGoalModalOpen, setIsHiringGoalModalOpen] = useState(false);
  const {
    showAggregateHiresCheckbox,
    showAggregateHires,
    setShowAggregateHires,
  } = useOpeningContext();

  const getProgress = (expected: number | null, actual: number | null) => {
    if (!actual || !expected) return 0;

    return (actual / expected) * 100;
  };

  if (!hiringGoal) return <span className="h-10" />;

  const {
    start_date: startDate,
    hire_by: endDate,
    hire_stats: { actual, expected },
  } = hiringGoal;

  return (
    <div className="flex w-full items-start justify-between gap-4 lg:w-fit lg:justify-center">
      <TimeframeInfo
        title={intl.formatMessage({ defaultMessage: 'Start date' })}
      >
        <Typography variant="body2">{formatShortDate(startDate)}</Typography>
      </TimeframeInfo>

      <TimeframeInfo title={intl.formatMessage({ defaultMessage: 'End date' })}>
        <Typography variant="body2">{formatShortDate(endDate)}</Typography>
      </TimeframeInfo>

      <div className="flex w-[370px] flex-col items-start gap-2 pl-4">
        <Typography variant="caption">
          <FormattedMessage defaultMessage="Hiring target" />
        </Typography>
        <div className="flex w-full items-center justify-between">
          <Typography variant="large2">
            {`${actual ?? '-'} of ${expected ?? '-'}`}
          </Typography>
          <Button
            variant="ghost"
            className="h-6 p-0 hover:bg-white"
            onClick={() => setIsHiringGoalModalOpen(true)}
          >
            <FormattedMessage defaultMessage="View All" />
            <img src={IconRight} alt="IconRight" />
          </Button>
        </div>

        <Progress
          value={getProgress(expected, actual)}
          className="bg-fx-primary"
        />

        <div className="flex items-center gap-2">
          <HiringOpeningsPopover />
          {showAggregateHiresCheckbox && (
            <AggregateHiresCheckbox
              checked={showAggregateHires}
              onCheckedChange={value => setShowAggregateHires(!!value)}
              label={intl.formatMessage({
                defaultMessage: 'Include aggregate hires',
              })}
              tooltipContent={intl.formatMessage({
                defaultMessage:
                  'This is total number of hires across all linked openings.',
              })}
            />
          )}
        </div>
      </div>
      <HiringGoalModal
        open={isHiringGoalModalOpen}
        onOpenChange={setIsHiringGoalModalOpen}
        hireATHiringGoalUrl={hireATHiringGoalUrl}
      />
    </div>
  );
}

HiringGoal.Skeleton = function HiringGoalSkeleton() {
  const intl = useIntl();
  return (
    <div className="flex items-start gap-4">
      <TimeframeInfo.Skeleton
        title={intl.formatMessage({ defaultMessage: 'Start date' })}
      />

      <TimeframeInfo.Skeleton
        title={intl.formatMessage({ defaultMessage: 'End date' })}
      />

      <div className="flex w-[270px] flex-col items-start gap-2 pl-4">
        <Typography variant="caption">
          <FormattedMessage defaultMessage="Hiring target" />
        </Typography>
        <div className="flex w-full items-center justify-between">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-24" />
        </div>
        <Skeleton className="h-2 w-full" />
        <div className="flex w-full items-center justify-between gap-1">
          <HiringOpeningsPopover.Skeleton />
          <Skeleton className="h-5 w-[178px]" />
        </div>
      </div>
    </div>
  );
};
